import { ReactNode, memo } from 'react';
import * as styles from './CommentCard.css';
import { useTimeAgo } from 'hooks/useTimeAgo';
import { toast } from 'react-toastify';
import { Box, Button, Card, Stack } from '@ff-it/ui';
import { UserAvatar } from 'components/UserAvatar';
import { createConfirmDialog } from 'components/Actions';
import { useAnchorUrl } from './useNavigationState';
import type { Comment } from './commentStore';
import { RichTextRenderer } from 'components/RichText';
import { getUserTitle } from 'modules/core/helpers';

const confirmDelete = createConfirmDialog('This comment will be removed for everyone. Are you sure?');

type CommentCardProps = {
  item: Comment;
  selected?: boolean;
  /** Only an author may delete, and only the server is authority on that. */
  canDelete?: boolean;
  onDelete?: (id: number) => Promise<void>;
};

export const CommentCard = memo(
  function CommentCard({ item, selected, canDelete, onDelete }: CommentCardProps): ReactNode {
    const anchorUrl = useAnchorUrl(item.id);
    const timeAgo = useTimeAgo(item.created_at);

    if (item.deleted) {
      return (
        <Card
          data-comment-id={item.id}
          active={selected}
          className={styles.card}
          flexShrink={0}
          aria-selected={selected}
        >
          <Box textSize="sm" color="secondary">
            This comment has been deleted.
          </Box>
        </Card>
      );
    }

    const remove = async (): Promise<void> => {
      if (!(await confirmDelete())) {
        return;
      }
      try {
        await onDelete?.(item.id);
      } catch {
        toast.error('Could not delete the comment');
      }
    };

    return (
      <Card data-comment-id={item.id} active={selected} className={styles.card} flexShrink={0} aria-selected={selected}>
        <Stack direction="horizontal" align="center">
          <UserAvatar user={item.created_by} size="xs" />
          <div className={styles.name}>{getUserTitle(item.created_by)}</div>
          <div className={styles.timeAgo}>{timeAgo}</div>
          <div className={styles.controls}>
            <Button
              icon="ellipsis"
              size="xxs"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(anchorUrl);
                toast.info('Link copied to clipboard');
              }}
            />
            {canDelete && (
              <Button
                testId={`delete-comment-${item.id}`}
                icon="trash"
                size="xxs"
                variant="ghost"
                onClick={() => void remove()}
              />
            )}
          </div>
        </Stack>
        <Box textSize="sm">
          <RichTextRenderer value={item.content} />
        </Box>

        {/* footer */}
      </Card>
    );
  },
  // Reducer merges deliberately retain an existing object when it is still
  // current. Anything that replaces it (including a tombstone) must render.
  (previous, next) =>
    previous.selected === next.selected && previous.item === next.item && previous.canDelete === next.canDelete,
);
